import React from 'react';

interface CommunicationPreferences {
    // Before purchase fields
    bef_pur_notes?: string;
    bef_pur_email_enabled?: boolean;
    bef_pur_email?: string;
    bef_pur_phone_enabled?: boolean;
    bef_pur_phone?: string;
    bef_pur_whatsapp_enabled?: boolean;
    bef_pur_whatsapp?: string;
    bef_pur_calendly_enabled?: boolean;
    bef_pur_calendly?: string;
    bef_pur_zoom_enabled?: boolean;
    bef_pur_zoom?: string;
    bef_pur_teams_enabled?: boolean;
    bef_pur_teams?: string;
    bef_pur_others_enabled?: boolean;
    bef_pur_others?: string;
    // After purchase fields
    aft_pur_notes?: string;
    aft_pur_email_enabled?: boolean;
    aft_pur_email?: string;
    aft_pur_phone_enabled?: boolean;
    aft_pur_phone?: string;
    aft_pur_whatsapp_enabled?: boolean;
    aft_pur_whatsapp?: string;
    aft_pur_calendly_enabled?: boolean;
    aft_pur_calendly?: string;
    aft_pur_zoom_enabled?: boolean;
    aft_pur_zoom?: string;
    aft_pur_teams_enabled?: boolean;
    aft_pur_teams?: string;
    aft_pur_others_enabled?: boolean;
    aft_pur_others?: string;
}

interface PartnerCommunicationPreferencesProps {
    partnerId: string | null;
    partnerName?: string | null;
    loadingCommPrefs: boolean;
    commPreferences: CommunicationPreferences | null;
    noPartnerMessage?: string;
    noPreferencesMessage?: string;
    mode?: 'before' | 'after'; // 'before' for bef_pur fields, 'after' for aft_pur fields
}

const PartnerCommunicationPreferences: React.FC<PartnerCommunicationPreferencesProps> = ({
    partnerId,
    partnerName,
    loadingCommPrefs,
    commPreferences,
    noPartnerMessage = "Partner information not available.",
    noPreferencesMessage,
    mode = 'before'
}) => {
    const displayPartnerName = partnerName || 'this partner';
    const defaultNoPreferencesMessage = `No communication preferences available for ${displayPartnerName}.`;
    
    // Determine which field prefix to use based on mode
    const prefix = mode === 'after' ? 'aft_pur' : 'bef_pur';
    
    // Helper function to get field value
    const getField = (fieldName: string) => {
        return commPreferences ? (commPreferences as any)[`${prefix}_${fieldName}`] : undefined;
    };
    
    // Helper function to check if field is enabled
    const isFieldEnabled = (fieldName: string) => {
        return commPreferences ? (commPreferences as any)[`${prefix}_${fieldName}_enabled`] : false;
    };

    if (loadingCommPrefs) {
        return (
            <div className="text-center" style={{ padding: '60px 20px' }}>
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!partnerId) {
        return (
            <div className="text-center" style={{ padding: '60px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📞</div>
                <p style={{ fontSize: 16, color: '#666' }}>{noPartnerMessage}</p>
            </div>
        );
    }

    if (!commPreferences) {
        return (
            <div className="text-center" style={{ padding: '60px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📭</div>
                <p style={{ fontSize: 16, color: '#666' }}>
                    {noPreferencesMessage || defaultNoPreferencesMessage}
                </p>
            </div>
        );
    }

    const hasAnyEnabled = 
        isFieldEnabled('email') ||
        isFieldEnabled('phone') ||
        isFieldEnabled('calendly') ||
        isFieldEnabled('zoom') ||
        isFieldEnabled('teams') ||
        isFieldEnabled('whatsapp') ||
        isFieldEnabled('others');

    return (
        <div style={{ padding: '0px 16px' }}>
            {/* Instructions - Plain text above mediums */}
            {getField('notes') && (
                <div style={{ marginBottom: 24 }}>
                    <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>
                        {getField('notes')}
                    </p>
                </div>
            )}

            {/* Email Group */}
            {isFieldEnabled('email') && getField('email') && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 12 }}>Email</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, maxWidth: '100%' }}>
                        <div
                            style={{
                                background: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: 8,
                                padding: 16,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                                e.currentTarget.style.borderColor = '#d0d0d0';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                e.currentTarget.style.borderColor = '#e0e0e0';
                            }}
                            onClick={() => window.location.href = `mailto:${getField('email')}`}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                <i className="bx bx-envelope" style={{ fontSize: 20, color: '#d93025' }}></i>
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>Email</div>
                            </div>
                            <a
                                href={`mailto:${getField('email')}`}
                                onClick={(e) => e.stopPropagation()}
                                style={{ color: '#1976d2', textDecoration: 'none', fontSize: 13, wordBreak: 'break-word', display: 'block' }}
                            >
                                {getField('email')}
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Phone Group (Phone and WhatsApp) */}
            {((isFieldEnabled('phone') && getField('phone')) ||
                (isFieldEnabled('whatsapp') && getField('whatsapp'))) && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 12 }}>Phone</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, maxWidth: '100%' }}>
                        {isFieldEnabled('phone') && getField('phone') && (
                            <div
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 8,
                                    padding: 16,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                                    e.currentTarget.style.borderColor = '#d0d0d0';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                }}
                                onClick={() => window.location.href = `tel:${getField('phone')}`}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <i className="bx bx-phone" style={{ fontSize: 20, color: '#34a853' }}></i>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>Phone</div>
                                </div>
                                <a
                                    href={`tel:${getField('phone')}`}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#1976d2', textDecoration: 'none', fontSize: 13, display: 'block' }}
                                >
                                    {getField('phone')}
                                </a>
                            </div>
                        )}

                        {isFieldEnabled('whatsapp') && getField('whatsapp') && (
                            <div
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 8,
                                    padding: 16,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                                    e.currentTarget.style.borderColor = '#d0d0d0';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                }}
                                onClick={() => {
                                    const whatsapp = getField('whatsapp');
                                    if (whatsapp) {
                                        window.open(`https://wa.me/${whatsapp.replace(/\D/g, '')}`, '_blank');
                                    }
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <i className="bx bxl-whatsapp" style={{ fontSize: 20, color: '#25d366' }}></i>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>WhatsApp</div>
                                </div>
                                <a
                                    href={getField('whatsapp') ? `https://wa.me/${getField('whatsapp')?.replace(/\D/g, '')}` : '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#1976d2', textDecoration: 'none', fontSize: 13, display: 'block' }}
                                >
                                    {getField('whatsapp')}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Schedule a Meeting Group (Zoom, Calendly, Teams) */}
            {((isFieldEnabled('zoom') && getField('zoom')) ||
                (isFieldEnabled('calendly') && getField('calendly')) ||
                (isFieldEnabled('teams') && getField('teams'))) && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 12 }}>Schedule a Meeting</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, maxWidth: '100%' }}>
                        {isFieldEnabled('calendly') && getField('calendly') && (
                            <div
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 8,
                                    padding: 16,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                                    e.currentTarget.style.borderColor = '#d0d0d0';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                }}
                                onClick={() => window.open(getField('calendly'), '_blank')}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <i className="bx bx-calendar" style={{ fontSize: 20, color: '#006bff' }}></i>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>Calendly</div>
                                </div>
                                <a
                                    href={getField('calendly')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#1976d2', textDecoration: 'none', fontSize: 13, display: 'block' }}
                                >
                                    Book appointment
                                </a>
                            </div>
                        )}

                        {isFieldEnabled('zoom') && getField('zoom') && (
                            <div
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 8,
                                    padding: 16,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                                    e.currentTarget.style.borderColor = '#d0d0d0';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                }}
                                onClick={() => window.open(getField('zoom'), '_blank')}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <i className="bx bx-video" style={{ fontSize: 20, color: '#2d8cff' }}></i>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>Zoom</div>
                                </div>
                                <a
                                    href={getField('zoom')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#1976d2', textDecoration: 'none', fontSize: 13, display: 'block' }}
                                >
                                    Join meeting
                                </a>
                            </div>
                        )}

                        {isFieldEnabled('teams') && getField('teams') && (
                            <div
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 8,
                                    padding: 16,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                                    e.currentTarget.style.borderColor = '#d0d0d0';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                }}
                                onClick={() => window.open(getField('teams'), '_blank')}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <i className="bx bx-chat" style={{ fontSize: 20, color: '#6264a7' }}></i>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>Microsoft Teams</div>
                                </div>
                                <a
                                    href={getField('teams')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#1976d2', textDecoration: 'none', fontSize: 13, display: 'block' }}
                                >
                                    Join meeting
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Other Group */}
            {isFieldEnabled('others') && getField('others') && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 12 }}>Other</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, maxWidth: '100%' }}>
                        <div
                            style={{
                                background: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: 8,
                                padding: 16,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s ease',
                                cursor: getField('others') && getField('others')?.toString().startsWith('http') ? 'pointer' : 'default'
                            }}
                            onMouseEnter={(e) => {
                                const others = getField('others');
                                if (others && others.toString().startsWith('http')) {
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                                    e.currentTarget.style.borderColor = '#d0d0d0';
                                }
                            }}
                            onMouseLeave={(e) => {
                                const others = getField('others');
                                if (others && others.toString().startsWith('http')) {
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                }
                            }}
                            onClick={() => {
                                const others = getField('others');
                                if (others && others.toString().startsWith('http')) {
                                    window.open(others.toString(), '_blank');
                                }
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                <i className="bx bx-link" style={{ fontSize: 20, color: '#666' }}></i>
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>Other</div>
                            </div>
                            {getField('others') && getField('others')?.toString().startsWith('http') ? (
                                <a
                                    href={getField('others')?.toString()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#1976d2', textDecoration: 'none', fontSize: 13, wordBreak: 'break-word', display: 'block' }}
                                >
                                    {getField('others')}
                                </a>
                            ) : (
                                <div style={{ color: '#666', fontSize: 13, wordBreak: 'break-word' }}>
                                    {getField('others') || ''}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Show message if no preferences are enabled */}
            {!hasAnyEnabled && (
                <div className="text-center" style={{ padding: '60px 20px', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', borderRadius: 20 }}>
                    <div style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }}>📭</div>
                    <p style={{ fontSize: 16, color: '#666', fontWeight: 500 }}>
                        No communication preferences are currently available for {displayPartnerName}.
                    </p>
                </div>
            )}
        </div>
    );
};

export default PartnerCommunicationPreferences;

